import { useCallback, useEffect, useState } from "react";

import { TAddressBookItem } from "@modules/address-book-search";

import { OrderFields } from "../../constants";
import useOrderFormik from "../useOrderFormik";

type TRecipientFormReturn = {
  isNewRecipientFormExpanded: boolean;
  isAddressSelectedFromAddressBook: boolean;
  expandNewRecipientForm: () => void;
  handleAddressBookItemSelected: (addressBookItem?: TAddressBookItem) => void;
  handleNewRecipientExpandChanged: (isExpanded: boolean) => void;
};

const useRecipientForm = (): TRecipientFormReturn => {
  const [isNewRecipientFormExpanded, setIsNewRecipientFormExpanded] = useState(false);
  const [isAddressSelectedFromAddressBook, setIsAddressSelectedFromAddressBook] = useState(false);

  const { values, setValues, setFieldValue, errors, setErrors } = useOrderFormik();

  const receiverName = values[OrderFields.RECEIVER_NAME];
  const receiverPhoneNumber = values[OrderFields.RECEIVER_PHONE_NUMBER];
  const receiverEmail = values[OrderFields.RECEIVER_EMAIL];
  const receiverLocker = values[OrderFields.RECEIVER_LOCKER];
  const receiverLockerName = values[OrderFields.RECEIVER_LOCKER_NAME];
  const receiverStreet = values[OrderFields.RECEIVER_STREET];
  const receiverHouseNo = values[OrderFields.RECEIVER_HOUSE_NO];
  const receiverFlatNo = values[OrderFields.RECEIVER_FLAT_NO];
  const receiverCity = values[OrderFields.RECEIVER_CITY];
  const receiverZipCode = values[OrderFields.RECEIVER_ZIP_CODE];

  const newAddressName = values[OrderFields.NEW_ADDRESS_NAME];
  const newAddressPhone = values[OrderFields.NEW_ADDRESS_PHONE];
  const newAddressEmail = values[OrderFields.NEW_ADDRESS_EMAIL];
  const newAddressLocker = values[OrderFields.NEW_ADDRESS_LOCKER];
  const newAddressLockerName = values[OrderFields.NEW_ADDRESS_LOCKER_NAME];
  const newAddressStreet = values[OrderFields.NEW_ADDRESS_STREET];
  const newAddressHouseNo = values[OrderFields.NEW_ADDRESS_HOUSE_NO];
  const newAddressFlatNo = values[OrderFields.NEW_ADDRESS_FLAT_NO];
  const newAddressCity = values[OrderFields.NEW_ADDRESS_CITY];
  const newAddressZipCode = values[OrderFields.NEW_ADDRESS_ZIP_CODE];

  // Hide order recipient errors if new recipient form is expanded.
  // Order recipient fields are not visible, when form is expanded, so there is not point in showing these errors
  useEffect(() => {
    if (isNewRecipientFormExpanded) {
      setErrors({
        [OrderFields.RECEIVER_NAME]: "",
        [OrderFields.RECEIVER_PHONE_NUMBER]: "",
        [OrderFields.RECEIVER_EMAIL]: "",
        [OrderFields.RECEIVER_LOCKER]: "",
        [OrderFields.RECEIVER_LOCKER_NAME]: "",
        [OrderFields.RECEIVER_STREET]: "",
        [OrderFields.RECEIVER_HOUSE_NO]: "",
        [OrderFields.RECEIVER_FLAT_NO]: "",
        [OrderFields.RECEIVER_CITY]: "",
        [OrderFields.RECEIVER_ZIP_CODE]: "",
      });
    }
  }, [isNewRecipientFormExpanded, setErrors]);

  const expandNewRecipientForm = useCallback(() => {
    setIsNewRecipientFormExpanded(true);
  }, []);

  const handleAddressBookItemSelected = useCallback(
    (addressBookItem?: TAddressBookItem) => {
      if (!addressBookItem) {
        return;
      }
      const newValues = {
        [OrderFields.HAS_NEW_ADDRESS]: false,
        [OrderFields.RECEIVER_ADDRESS_ID]: addressBookItem?.id,
        [OrderFields.RECEIVER_NAME]: addressBookItem?.contactName || "",
        [OrderFields.RECEIVER_PHONE_NUMBER]: addressBookItem?.contactPhone || "",
        [OrderFields.RECEIVER_EMAIL]: addressBookItem?.contactEmail || "",
        [OrderFields.RECEIVER_LOCKER]: addressBookItem?.lockerId || "",
        [OrderFields.RECEIVER_LOCKER_NAME]: addressBookItem?.locker?.name || "",
        [OrderFields.RECEIVER_STREET]: addressBookItem?.street || "",
        [OrderFields.RECEIVER_HOUSE_NO]: addressBookItem?.streetNumber || "",
        [OrderFields.RECEIVER_FLAT_NO]: addressBookItem?.flatNo || "",
        [OrderFields.RECEIVER_CITY]: addressBookItem?.city || "",
        [OrderFields.RECEIVER_ZIP_CODE]: addressBookItem?.zip ?? addressBookItem?.locker?.address.postalCode ?? "",
      };

      setValues((oldValues) => ({ ...oldValues, ...newValues }));

      const fieldErrors = {
        ...errors,
        [OrderFields.RECEIVER_NAME]: "",
        [OrderFields.RECEIVER_PHONE_NUMBER]: "",
        [OrderFields.RECEIVER_EMAIL]: "",
        [OrderFields.RECEIVER_LOCKER]: "",
        [OrderFields.RECEIVER_LOCKER_NAME]: "",
        [OrderFields.RECEIVER_STREET]: "",
        [OrderFields.RECEIVER_HOUSE_NO]: "",
        [OrderFields.RECEIVER_FLAT_NO]: "",
        [OrderFields.RECEIVER_CITY]: "",
        [OrderFields.RECEIVER_ZIP_CODE]: "",
      };
      setErrors(fieldErrors);

      setIsNewRecipientFormExpanded(false);
      setIsAddressSelectedFromAddressBook(true);
    },
    [errors, setErrors, setValues]
  );

  const handleNewRecipientExpandChanged = useCallback(
    (isExpanded: boolean) => {
      setIsNewRecipientFormExpanded(isExpanded);

      if (!isExpanded) {
        setFieldValue(OrderFields.HAS_NEW_ADDRESS, false);
        return;
      }

      setValues((oldValues) => ({
        ...oldValues,
        [OrderFields.HAS_NEW_ADDRESS]: true,
        [OrderFields.NEW_ADDRESS_NAME]: receiverName || newAddressName,
        [OrderFields.NEW_ADDRESS_PHONE]: receiverPhoneNumber || newAddressPhone,
        [OrderFields.NEW_ADDRESS_EMAIL]: receiverEmail || newAddressEmail,
        [OrderFields.NEW_ADDRESS_LOCKER]: receiverLocker || newAddressLocker,
        [OrderFields.NEW_ADDRESS_LOCKER_NAME]: receiverLockerName || newAddressLockerName,
        [OrderFields.NEW_ADDRESS_STREET]: receiverStreet || newAddressStreet,
        [OrderFields.NEW_ADDRESS_HOUSE_NO]: receiverHouseNo || newAddressHouseNo,
        [OrderFields.NEW_ADDRESS_FLAT_NO]: receiverFlatNo || newAddressFlatNo,
        [OrderFields.NEW_ADDRESS_CITY]: receiverCity || newAddressCity,
        [OrderFields.NEW_ADDRESS_ZIP_CODE]: receiverZipCode || newAddressZipCode,
      }));
    },
    [
      newAddressCity,
      newAddressEmail,
      newAddressFlatNo,
      newAddressHouseNo,
      newAddressLocker,
      newAddressLockerName,
      newAddressName,
      newAddressPhone,
      newAddressStreet,
      newAddressZipCode,
      receiverCity,
      receiverEmail,
      receiverFlatNo,
      receiverHouseNo,
      receiverLocker,
      receiverLockerName,
      receiverName,
      receiverPhoneNumber,
      receiverStreet,
      receiverZipCode,
      setFieldValue,
      setValues,
    ]
  );

  return {
    isNewRecipientFormExpanded,
    isAddressSelectedFromAddressBook,
    expandNewRecipientForm,
    handleAddressBookItemSelected,
    handleNewRecipientExpandChanged,
  };
};
export default useRecipientForm;
